import { useState } from 'react';
import { StyleProp, StyleSheet, TextStyle, ViewStyle } from 'react-native';
import { fullWidth, scale } from '@components/frame';
import { font } from '@styles/fonts';
import { useSelector } from 'react-redux';
import {
  getLinkingModalConfig,
  isPhoneNative,
  ncFileStatuses,
  Popup,
} from '../../../../../../../../../../helpers';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { getBoxShadowStyle } from '@styles/shadow';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { BatchDetailsScreenName } from '@v2/pages/batch/details/navigation/config';
import { BatchDetailsScreen } from '@v2/pages/batch/details/common/enums/batchDetails.enum';
import { LevelSubScreen, Perspective } from '@v2/pages/common/enums';
import { useHandleNavigationMachinesHeader } from '@v2/pages/machines/hooks/useHandleNavigationMachineHeader';
import { useNavigationCheck } from '@v2/pages/machines/hooks/useNavigationCheck';
import { useDocumentsNavigation } from '@v2/pages/common/hooks/useDocumentsNavigation';
import { NowSubScreen } from '@v2/pages/machines/common/enums';
import { selectNcFileId, selectPartId } from '@reducers/details';
import { selectIsWithERP } from '@reducers/membership';
import { selectSelectedItem, selectMachines } from '@reducers/machines';
import { selectServerOffset } from '@reducers/time';
import { selectSingleBatchData } from '@reducers/batch';
import { useRoute } from '@react-navigation/native';
import { MachineEventSearchItem } from '@interfaces/machines';
import { BatchDetailsBlockContentProps } from '@v2/pages/batch/details/modules/batch/components/BatchDetailsBlock/BatchDetailsBlockContent';
import React from 'react';
import NCFileType from '@components/common/NCFileType';
import ProductionInformation from '@components/productionInformation';
import ProductivityStatusInfo from '@v2/pages/batch/details/modules/batch/components/ProductivityStatusInfo/ProductivityStatusInfo';

export interface UseManageBatchDetailsBlockContentParams extends Pick<BatchDetailsBlockContentProps, 'batch' | 'C' | 'withImages' | 'maxDetailsWidth' | 'bg'> {}

export const useManageBatchDetailsBlockContent = ({ batch, C, bg = 'Background/Light/Field' }: UseManageBatchDetailsBlockContentParams) => {
  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();
  const { isMachinesScreen } = useHandleNavigationMachinesHeader();
  const { safeIsNowSubScreenCheck } = useNavigationCheck();
  const { handleNowScreenNavigation } = useDocumentsNavigation();

  const ncFileId = useSelector(selectNcFileId);
  const partId = useSelector(selectPartId);
  const isWithERP = useSelector(selectIsWithERP);
  const _selectedItem = useSelector(selectSelectedItem);
  const machines = useSelector(selectMachines);
  const offset = useSelector(selectServerOffset) ?? 0;
  const batchData = useSelector(selectSingleBatchData);
  const batchLoaded = batchData?.wasLoaded;

  const [events, setEvents] = useState<MachineEventSearchItem[]>([]);

  const selectedItem =
    isMachinesScreen || batch?.machineId === _selectedItem.machineId ? _selectedItem : machines?.find((m) => m.id === batch?.machineId) ?? {};

  const route = useRoute();
  const isBatchPerspective = route.params?.perspective === Perspective.BATCH;

  const borderStyles: StyleProp<ViewStyle> = {
    borderBottomColor: C('Hairline/Light/No trans'),
    borderBottomWidth: StyleSheet.hairlineWidth,
  };

  const menuItemTextStyles: StyleProp<TextStyle>[] = [font['Body'], { color: C('Label/Light/Primary') }];

  const borderRadiusConfig = { borderRadius: scale(SCREEN_BORDER_RADIUS) };

  const onProductivityStatusBlockClick = () => {
    Popup({
      Component: (p: any) => <ProductivityStatusInfo {...{ ...p, selectedItem, batch, C, events }} />,
      ...getLinkingModalConfig(scale, fullWidth),
      width: 450,
      useFullHeight: isPhoneNative,
    });
  };

  const onBatchLinkClick = () => {
    if ([ncFileStatuses.Unknown, ncFileStatuses.Ignored, ncFileStatuses.Setup].includes(batch.ncFileStatus)) {
      Popup({
        Component: (p: any) => (
          <NCFileType
            {...{ ...p, selectedItem, productionBatchId: batch.productionBatchId, ncFileId: batch?.ncFileId, ncFileName: batch?.ncFileName }}
          />
        ),
        ...getLinkingModalConfig(scale, fullWidth),
      });
      return;
    }

    Popup({
      Component: (p: any) => (
        <ProductionInformation
          {...{
            ...p,
            selectedItem,
            productionBatchId: batch?.productionBatchId,
            selectedNCFileId: isMachinesScreen ? selectedItem?.machineStatus?.ncFileId : ncFileId,
            partId,
          }}
        />
      ),
      ...getLinkingModalConfig(scale, fullWidth),
    });
  };

  const onBatchNumberClick = () => {
    navigateToMainDetailsScreen({
      mainScreen: BatchDetailsScreenName,
      subScreen: BatchDetailsScreen.BATCH,
      params: {
        perspective: Perspective.BATCH,
        batchNumber: batch?.batchNumber,
        batchId: batch?.productionBatchId,
        ncFileStatus: batch?.ncFileStatus,
      },
    });
  };

  const onBatchImageClick = (selectedImageKey: string) => {
    if (safeIsNowSubScreenCheck) {
      handleNowScreenNavigation({ nowSubScreen: NowSubScreen.BATCH, params: { selectedImageKey } });
      return;
    }
    navigateToMainDetailsScreen({
      mainScreen: BatchDetailsScreenName,
      subScreen: BatchDetailsScreen.BATCH,
      levelSubScreen: LevelSubScreen.DOCUMENTS,
      params: {
        perspective: Perspective.BATCH,
        batchNumber: batch?.batchNumber,
        batchId: batch?.productionBatchId,
        ncFileStatus: batch?.ncFileStatus,
        selectedImageKey,
      },
    });
  };

  const batchLinkColumnNameBasedOnNcFileStatus = {
    [ncFileStatuses.Unknown]: 'Update NC File Type',
    [ncFileStatuses.Ignored]: 'NC File Type',
    [ncFileStatuses.Setup]: 'NC File Type',
  };

  const batchLinkColumnName = batchLinkColumnNameBasedOnNcFileStatus[batch?.ncFileStatus ?? ''] ?? 'Batch Link';

  const offsetInSeconds = Math.floor(offset / 1000);

  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });

  return {
    isMachinesScreen,
    isWithERP,
    selectedItem,
    offset,
    batchLoaded,
    events,
    setEvents,
    isBatchPerspective,
    borderStyles,
    menuItemTextStyles,
    borderRadiusConfig,
    onProductivityStatusBlockClick,
    onBatchLinkClick,
    onBatchNumberClick,
    onBatchImageClick,
    batchLinkColumnName,
    offsetInSeconds,
    shadowStyles,
  };
};
